import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Mail, ArrowLeft, X } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

interface EmailVerificationOverlayProps {
  actionName: string;
  onClose?: () => void;
}

export function EmailVerificationOverlay({ actionName, onClose }: EmailVerificationOverlayProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isResending, setIsResending] = useState(false);

  const handleGoBack = () => {
    if (onClose) {
      onClose();
    }
    navigate(-1);
  };

  const handleResendEmail = async () => {
    setIsResending(true);
    try {
      const response = await fetch('/api/v1/auth/resend-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        alert('Το email επαλήθευσης στάλθηκε ξανά!');
      } else {
        alert('Σφάλμα κατά την αποστολή του email.');
      }
    } catch (error) {
      console.error('Error resending verification email:', error);
      alert('Σφάλμα κατά την αποστολή του email.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={handleGoBack} />
      
      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 border border-red-200">
        {/* Close button */}
        <button
          onClick={handleGoBack}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header with red background */}
        <div className="bg-red-50 border-b border-red-200 px-6 py-4 rounded-t-lg">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-semibold text-red-800">
                Επαλήθευση Email Απαιτείται
              </h3>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          <div className="mb-4">
            <p className="text-gray-700 mb-3">
              Για να μπορέσετε να <strong>{actionName}</strong>, πρέπει πρώτα να επαληθεύσετε τη διεύθυνση email σας.
            </p>
            
            <p className="text-gray-600 text-sm mb-4">
              Ένα email επαλήθευσης έχει σταλεί στη διεύθυνση:{' '}
              <strong className="text-gray-800">{user?.email}</strong>
            </p>
          </div>

          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
            <h4 className="text-sm font-medium text-blue-800 mb-2 flex items-center">
              <Mail className="w-4 h-4 mr-2" />
              Οδηγίες Επαλήθευσης
            </h4>
            <ol className="text-sm text-blue-700 space-y-1 ml-6 list-decimal">
              <li>Ελέγξτε τα εισερχόμενα email σας</li>
              <li>Αναζητήστε email από "Pathiety"</li>
              <li>Κάντε κλικ στον σύνδεσμο επαλήθευσης</li>
              <li>Επιστρέψτε και δοκιμάστε ξανά</li>
            </ol>
          </div>

          {/* Actions */}
          <div className="flex flex-col space-y-3">
            <button
              onClick={handleResendEmail}
              disabled={isResending}
              className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Mail className="w-4 h-4 mr-2" />
              {isResending ? 'Αποστέλλεται...' : 'Αποστολή Νέου Email'}
            </button>
            
            <button
              onClick={handleGoBack}
              className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm bg-gray-600 text-sm font-medium text-white hover:bg-gray-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Επιστροφή
            </button>
          </div>

          {/* Help text */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              Δεν λάβατε το email; Ελέγξτε τον φάκελο spam ή επικοινωνήστε μαζί μας για βοήθεια.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}